
export interface StreamStartEvent {
    type: 'start';
    messageId: string;
    messageMetadata: {
        threadId: string;
        assistantMessageId?: string;
        mode?: string;
    };
}

export interface StreamStepEvent {
    type: 'data-step';
    id: string;
    data: {
        node?: string;
        displayName?: string;
        status: 'running' | 'done' | 'stopped';
        durationMs?: number;
    };
}

export interface StreamTextDeltaEvent {
    type: 'text-delta';
    id: string;
    delta: string;
}

export interface StreamAttachmentEvent {
    type: 'data-attachment';
    data: {
        file_id: string;
        name: string;
        url: string;
    };
}

export type AiStreamEvent =
    | StreamStartEvent
    | StreamStepEvent
    | StreamTextDeltaEvent
    | StreamAttachmentEvent
    | { type: 'text-start'; id: string }
    | { type: 'text-end'; id: string }
    | { type: 'finish'; finishReason?: string }
    | { type: 'abort'; reason?: string }
    | { type: 'error'; errorText?: string }
    | { type: string; [key: string]: any };

export async function readAiStream(
    response: Response,
    onEvent: (event: AiStreamEvent) => void
): Promise<void> {
    if (!response.body) {
        throw new Error('Stream body is empty');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');

    let buffer = '';
    let receivedDone = false;

    const processFrame = (frame: string) => {
        const data = frame
            .split('\n')
            .filter(line => line.startsWith('data:'))
            .map(line => line.slice(5).trimStart())
            .join('\n');

        if (!data) return;

        if (data.trim() === '[DONE]') {
            receivedDone = true;
            return;
        }

        const event = JSON.parse(data) as AiStreamEvent;
        onEvent(event);
    };

    const processBuffer = () => {
        // SSE может использовать CRLF, поэтому нормализуем переносы.
        buffer = buffer.replace(/\r\n/g, '\n');

        let separatorIndex = buffer.indexOf('\n\n');

        while (separatorIndex !== -1) {
            const frame = buffer.slice(0, separatorIndex);
            buffer = buffer.slice(separatorIndex + 2);

            processFrame(frame);

            separatorIndex = buffer.indexOf('\n\n');
        }
    };

    try {
        while (true) {
            const { value, done } = await reader.read();

            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            processBuffer();
        }

        buffer += decoder.decode();
        processBuffer();

        if (buffer.trim()) {
            processFrame(buffer);
        }

        if (!receivedDone) {
            throw new Error('Stream ended without [DONE]');
        }
    } finally {
        reader.releaseLock();
    }
}
